import { http } from "@/api/client";

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA", "SVG", "IFRAME"]);
const HAS_LETTER = /[A-Za-zÀ-ÿ]/;
const LANG_KEY = "dds_lang";
export const LANGS = [
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "es", label: "Español", flag: "🇪🇸" },
];

class Translator {
  constructor() {
    this.lang = "fr";
    this.records = new Map();
    this.attrRecords = new Map();
    this.cache = new Map();
    this.observer = null;
    this.timer = null;
    this.busy = false;
  }

  getStoredLang() {
    const l = localStorage.getItem(LANG_KEY);
    return LANGS.some((x) => x.code === l) ? l : "fr";
  }

  setLanguage(lang) {
    localStorage.setItem(LANG_KEY, lang);
    this.apply(lang);
    window.dispatchEvent(new CustomEvent("dds-lang-changed", { detail: lang }));
  }

  apply(lang) {
    if (lang === "fr") {
      this.restore();
      return;
    }
    if (this.lang !== "fr" && this.lang !== lang) this.restore();
    this.lang = lang;
    this.translatePage();
    this.startObserver();
  }

  restore() {
    this.lang = "fr";
    this.stopObserver();
    for (const [node, rec] of this.records) {
      if (node.isConnected && node.nodeValue === rec.translated) node.nodeValue = rec.original;
    }
    for (const [el, rec] of this.attrRecords) {
      if (el.isConnected && el.getAttribute("placeholder") === rec.translated) el.setAttribute("placeholder", rec.original);
    }
    this.records.clear();
    this.attrRecords.clear();
  }

  collectNodes() {
    const out = [];
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        const text = node.nodeValue;
        if (!text || !text.trim() || !HAS_LETTER.test(text)) return NodeFilter.FILTER_REJECT;
        let el = node.parentElement;
        while (el) {
          if (SKIP_TAGS.has(el.tagName) || el.hasAttribute("data-no-translate") || el.classList.contains("notranslate")) {
            return NodeFilter.FILTER_REJECT;
          }
          el = el.parentElement;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    let n;
    while ((n = walker.nextNode())) out.push(n);
    return out;
  }

  async fetchTranslations(texts, lang) {
    const missing = texts.filter((t) => !this.cache.has(`${lang}::${t}`));
    for (let i = 0; i < missing.length; i += 150) {
      const chunk = missing.slice(i, i + 150);
      const res = await http.post("/translate", { texts: chunk, target: lang });
      res.data.translations.forEach((tr, j) => this.cache.set(`${lang}::${chunk[j]}`, tr));
    }
    return texts.map((t) => this.cache.get(`${lang}::${t}`) ?? t);
  }

  async translatePage() {
    if (this.busy || this.lang === "fr") return;
    this.busy = true;
    const lang = this.lang;
    try {
      const nodes = this.collectNodes();
      const jobs = [];
      for (const node of nodes) {
        const rec = this.records.get(node);
        if (rec && node.nodeValue === rec.translated) continue;
        const raw = node.nodeValue;
        const trimmed = raw.trim();
        jobs.push({ node, raw, trimmed });
      }
      const inputs = Array.from(document.querySelectorAll("input[placeholder], textarea[placeholder]"));
      const attrJobs = [];
      for (const el of inputs) {
        const rec = this.attrRecords.get(el);
        const val = el.getAttribute("placeholder");
        if (!val || !HAS_LETTER.test(val)) continue;
        if (rec && val === rec.translated) continue;
        if (el.closest("[data-no-translate]")) continue;
        attrJobs.push({ el, val });
      }
      if (!jobs.length && !attrJobs.length) return;

      const uniq = [...new Set([...jobs.map((j) => j.trimmed), ...attrJobs.map((j) => j.val)])];
      await this.fetchTranslations(uniq, lang);
      if (this.lang !== lang) return;

      this.stopObserver();
      for (const { node, raw, trimmed } of jobs) {
        if (!node.isConnected || node.nodeValue !== raw) continue;
        const tr = this.cache.get(`${lang}::${trimmed}`);
        if (!tr || tr === trimmed) continue;
        const lead = raw.match(/^\s*/)[0];
        const tail = raw.match(/\s*$/)[0];
        const newVal = lead + tr + tail;
        node.nodeValue = newVal;
        this.records.set(node, { original: raw, translated: newVal });
      }
      for (const { el, val } of attrJobs) {
        if (!el.isConnected) continue;
        const tr = this.cache.get(`${lang}::${val}`);
        if (!tr || tr === val) continue;
        el.setAttribute("placeholder", tr);
        this.attrRecords.set(el, { original: val, translated: tr });
      }
      if (this.lang !== "fr") this.startObserver();
    } catch (e) {
      // API indisponible : le site reste en français
    } finally {
      this.busy = false;
      }
  }

  startObserver() {
    if (this.observer) return;
    this.observer = new MutationObserver(() => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.translatePage(), 250);
    });
    this.observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  stopObserver() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    clearTimeout(this.timer);
  }
}

export const translator = new Translator();
